import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Save } from 'lucide-react';
import { API, adminAuthHeader } from '../api';
import ImageUploader from './ImageUploader';

const Field = ({ label, hint, ...props }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    <input {...props} className={`w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8] ${props.className || ''}`} />
    {hint && <p className="text-[11px] text-gray-400 mt-1">{hint}</p>}
  </div>
);

const InvoiceSettingsManager = () => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState('');
  const [states, setStates] = useState([]);

  useEffect(() => { load(); }, []);
  const load = async () => {
    const [s, st] = await Promise.all([
      axios.get(`${API}/admin/invoice-settings`, { headers: adminAuthHeader() }),
      axios.get(`${API}/admin/india-states`, { headers: adminAuthHeader() }),
    ]);
    setForm(s.data);
    setStates(st.data || []);
  };
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const onStateChange = (name) => {
    const st = states.find((x) => x.name === name);
    setForm((f) => ({ ...f, state: name, state_code: st?.code || '' }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/invoice-settings`, form, { headers: adminAuthHeader() });
      setForm(res.data);
      setSavedAt(new Date().toLocaleTimeString());
    } finally { setSaving(false); }
  };

  if (!form) return <div className="text-sm text-gray-500 py-8 text-center">Loading…</div>;

  return (
    <div data-testid="invoice-settings-manager">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Invoice Settings</h2>
          <p className="text-sm text-gray-500">These details appear on every invoice PDF. Set them once — they populate on every new bill you generate.</p>
        </div>
        <div className="flex items-center gap-3">
          {savedAt && <span className="text-xs text-emerald-600">Saved at {savedAt}</span>}
          <button
            onClick={save}
            disabled={saving}
            data-testid="invoice-settings-save"
            className="flex items-center gap-1.5 bg-[#0d64a8] hover:bg-[#0a548e] text-white text-sm px-4 py-2 rounded-md disabled:opacity-60"
          >
            {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14} />} Save
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-5 mb-5">
        <h3 className="text-xs font-semibold text-gray-800 uppercase tracking-widest mb-3">Company / Seller</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <Field label="Company Name" value={form.company_name || ''} onChange={(e) => upd('company_name', e.target.value)} />
          </div>
          <Field label="Website (optional)" value={form.website || ''} onChange={(e) => upd('website', e.target.value)} placeholder="www.travelsera.com" />
          <div className="md:col-span-2">
            <Field label="Address Line 1" value={form.address_line1 || ''} onChange={(e) => upd('address_line1', e.target.value)} />
          </div>
          <Field label="Address Line 2 (optional)" value={form.address_line2 || ''} onChange={(e) => upd('address_line2', e.target.value)} />
          <Field label="City" value={form.city || ''} onChange={(e) => upd('city', e.target.value)} />
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">State</label>
            <select
              value={form.state || ''}
              onChange={(e) => onStateChange(e.target.value)}
              data-testid="settings-state-select"
              className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8] bg-white"
            >
              <option value="">Select state</option>
              {states.map((s) => <option key={s.code} value={s.name}>{s.name} ({s.code})</option>)}
            </select>
            <p className="text-[11px] text-gray-400 mt-1">Determines CGST/SGST vs IGST when tax breakdown is enabled.</p>
          </div>
          <Field label="Pincode" value={form.pincode || ''} onChange={(e) => upd('pincode', e.target.value)} />
          <Field label="Phone" value={form.phone || ''} onChange={(e) => upd('phone', e.target.value)} />
          <Field label="Email" value={form.email || ''} onChange={(e) => upd('email', e.target.value)} />
          <Field label="GSTIN" value={form.gstin || ''} onChange={(e) => upd('gstin', e.target.value)} placeholder="06AABCT1234N1Z5" />
          <Field label="PAN" value={form.pan || ''} onChange={(e) => upd('pan', e.target.value)} />
          <Field label="Signatory Name (optional)" value={form.signatory_name || ''} onChange={(e) => upd('signatory_name', e.target.value)} />
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-5 mb-5">
        <h3 className="text-xs font-semibold text-gray-800 uppercase tracking-widest mb-3">Bank / Payment</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Bank Name" value={form.bank_name || ''} onChange={(e) => upd('bank_name', e.target.value)} />
          <Field label="Account Holder" value={form.account_holder || ''} onChange={(e) => upd('account_holder', e.target.value)} />
          <Field label="Account Number" value={form.account_number || ''} onChange={(e) => upd('account_number', e.target.value)} />
          <Field label="IFSC" value={form.ifsc || ''} onChange={(e) => upd('ifsc', e.target.value)} />
          <Field label="Branch" value={form.branch || ''} onChange={(e) => upd('branch', e.target.value)} />
        </div>
        <div className="mt-4 pt-4 border-t border-gray-100" data-testid="settings-payment-qr-block">
          <ImageUploader
            value={form.payment_qr_image || ''}
            onChange={(url) => upd('payment_qr_image', url)}
            label="Payment QR Code"
            hint="Upload once — this QR is shown on every itinerary's Payment Details PDF. Square image, 300×300 or larger."
            height="h-48"
          />
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-5 mb-5">
        <h3 className="text-xs font-semibold text-gray-800 uppercase tracking-widest mb-3">Numbering & Tax</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Invoice Prefix" value={form.invoice_prefix || ''} onChange={(e) => upd('invoice_prefix', e.target.value)} hint='Numbers will look like "TS/25-26/0001"' />
          <Field type="number" label="Next Invoice Serial" value={form.next_invoice_serial || 1} onChange={(e) => upd('next_invoice_serial', Number(e.target.value) || 1)} hint="Auto-increments after each invoice." />
          <Field type="number" step="1" label="Default GST Rate (%)" value={form.default_gst_rate ?? 18} onChange={(e) => upd('default_gst_rate', Number(e.target.value) || 0)} hint="18% is applied on commission unless changed." />
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <h3 className="text-xs font-semibold text-gray-800 uppercase tracking-widest mb-3">Terms & Conditions (footer of every invoice)</h3>
        <textarea
          value={form.terms_and_conditions || ''}
          onChange={(e) => upd('terms_and_conditions', e.target.value)}
          rows={7}
          data-testid="settings-terms"
          className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8] resize-none font-mono"
        />
        <p className="text-[11px] text-gray-400 mt-1">One point per line. These render as a bulleted list at the bottom of the PDF.</p>
      </div>
    </div>
  );
};

export default InvoiceSettingsManager;
